import React, {useMemo} from "react";
import WatchIcon from "@atlaskit/icon/glyph/watch";
import CheckIcon from "@atlaskit/icon/glyph/check";

import sortBy from "ramda/src/sortBy";
import mergeLeft from "ramda/src/mergeLeft";
import propOr from "ramda/src/propOr";
import pipe from "ramda/src/pipe";
import map from "ramda/src/map";
import prop from "ramda/src/prop";
import equals from "ramda/src/equals";

import Api from "../../services/Api";
import { Grid } from "../../UI/Grid";
import { L2Selector, L3Selector } from "../../common/network/NetworkSelectors";
import { Button } from "../../UI/Button";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import useAuth from "../../routes/login/AuthContext";
import { normalize } from "../../helpers/normalize";
import { useNetwork } from "../../hooks/useNetwork";

import { ReadOnlyWrapper } from "../../components/ReadOnlyWrapper";
import { SliderQuestionsTable } from "../../components/SliderQuestionsTable";
import { QuestionRemovalModal } from "../../components/QuestionRemovalModal";
import { SelectQuestionsTable } from "../../components/SelectQuestionsTable";
import { FreeTextQuestionsTable } from "../../components/FreeTextQuestionsTable";
import { PreviewQuestionsModal } from "../../components/PreviewQuestionsModal";
import { ApplyToOthersModal } from "../../components/ApplyToOthersModal";
import { Link } from "../../components/Link";
import { Typography } from "../../UI/Typography";
import { ModalProvider } from "../../contexts/ModalContext";
import { CopyQuestionsToOtherNetworks } from "../../components/CopyQuestionsToOtherNetworks";
import { CLIENTS_EMAIL_ADDRESS } from "../../constants/general";

export const initialQuestionState = [
  {
    questionId: 0,
    questionNumber: 1,
    questionText: "What time do you prefer to go to bed during the week?",
    reverseText: "What time do you want your roommate to go to bed during the week?",
    lowText: "9 PM",
    highText: "3 AM",
  },
];

export const LifestyleQuestion = () => {
  const [unitType, setUnitType] = React.useState<number>();
  const [subnetwork, setSubnetwork] = React.useState<any>();
  const [removeInfo, setRemoveInfo] = React.useState(null);
  const [questionsLoading, setQuestionsLoading] = React.useState(true);
  const [questions, setQuestions] = React.useState<Record<string, any>>({});
  const [lifestyleTemplateSelection, setLifestyleTemplateSelection] = React.useState<any>();
  const [initialQuestions, setInitialQuestions] = React.useState<any>({});
  const [applyToOthers, setApplyToOthers] = React.useState(false);
  const [previewQuestions, setPreviewQuestions] = React.useState(false);
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { getL3, getL2 } = useNetwork();
  const { getAuthToken, isSuperAdmin, isSuperAdminRead } = useAuth();
  const networkData = {
    l1Id: selectedNetworkId,
    l2Id: subnetwork,
    l3Id: unitType,
  };
  const editDisabled = !!lifestyleTemplateSelection;
  const showTagColumn = isSuperAdmin();

  const l2 = React.useMemo(() => getL2(selectedNetworkId, subnetwork), [
    selectedNetworkId,
    subnetwork,
    getL2,
  ]);

  const isDataChange = React.useMemo(() => !equals(initialQuestions, questions) && !questionsLoading, [
    normalize(initialQuestions),
    normalize(questions),
    questionsLoading,
  ]);

  const applyToOtherUnitAppearance = React.useMemo(() => (isDataChange ? "warning" : "default"), [isDataChange]);

  const fetchQuestions = async (noLoading = false) => {
    setQuestionsLoading(!noLoading);
    const { result } = await Api.getQuestionsForNetwork(networkData, await getAuthToken());

    const getSliderQuestions = pipe<any, any, any>(
      propOr([], "SLIDER"),
      map(({ answers, ...question }) => ({
        ...question,
        answers: sortBy<any>(prop("answerOrder"), answers),
      }))
    );

    const getSelectQuestions = pipe<any, any, any>(
      propOr([], "SELECT"),
      map(({ answers, ...question }) => ({
        ...question,
        answers: sortBy<any>(prop("answerOrder"), answers),
      }))
    );

    const getFreeTextQuestions = pipe<any, any, any>(
      propOr([], "FREE_TEXT"),
      map(({ answers, ...question }) => ({
        ...question,
        answers: sortBy<any>(prop("answerOrder"), answers),
      }))
    );

    const questions = {
      SLIDER: getSliderQuestions(result),
      SELECT: getSelectQuestions(result),
      FREE_TEXT: getFreeTextQuestions(result),
    };

    if (networkData.l3Id !== undefined) {
      const l3 = getL3(networkData.l1Id, networkData.l2Id, networkData?.l3Id);

      setLifestyleTemplateSelection(l3?.lifestyleTemplateSelection);
    }

    setQuestions(mergeLeft(questions));
    setQuestionsLoading(false);

    if (!noLoading) {
      setInitialQuestions(questions);
    }
  };

  const updateState = (changedQuestions: any) =>
    setQuestions((questions: any) => ({
      ...questions,
      ...changedQuestions,
    }));

  // ~~ reorder questions ~~
  const onReorder = React.useCallback(
    async ({ type, items }) => {
      updateState({ [type]: items });

      Api.updateNetworkQuestionOrder(networkData, await getAuthToken(), {
        ...networkData,
        questionType: type,
        order: items.reduce(
          (pv: any, cv: { id: any; questionOrder: any }) => ({
            ...pv,
            [cv.id]: cv.questionOrder,
          }),
          {}
        ),
      });
    },
    [networkData]
  );
  // ~~ save question ~~
  const onSave = React.useCallback(
    async (item: { id: any; questionType: any; tag: any }) => {
      const response = await Api.updateNetworkQuestionData(networkData, await getAuthToken(), {
        ...networkData,
        ...item,
        tagId: item?.tag?.id,
        tag: null,
      });

      if (response.error) {
        return {
          error: response.message || "Something went wrong, please try again later.",
        };
      }
      if (item.id) {
        const type = item.questionType;
        const items = [...questions[type]];
        const index = items.findIndex(({ id }) => id === item.id);
        if (index > -1) {
          items[index] = item;
        }
        updateState({ [type]: items } as any);
      } else {
        fetchQuestions(true);
      }

      return { success: true };
    },
    [networkData, questions]
  );
  // ~~ remove question ~~
  const onRemove = React.useCallback(
    async (item: { id: any; questionType: any }) => {
      const response = await Api.updateNetworkQuestionData(networkData, await getAuthToken(), { id: item.id });

      if (response.error) {
        return response;
      }
      const type = item.questionType;
      const items = questions[type];
      updateState({ [type]: items.filter(({ id }: any) => id !== item.id) });

      return true;
    },
    [networkData, questions]
  );

  // subnetwork selected
  React.useEffect(() => {
    if (subnetwork && unitType) {
      setQuestions({});
      fetchQuestions();
    }
  }, [subnetwork, unitType]);

  // on l1 changed
  React.useEffect(() => {
    setSubnetwork(null);
  }, [selectedNetworkId]);

  const isReadOnly = useMemo(() => !!(l2?.matchingRoundStatus === "ARCHIVED" || l2?.matchingRoundStatus === "CLOSED") , [l2]);

  const handlePreviewQuestions = () => {
    setPreviewQuestions(true);
  };

  const handleApplyToOther = () => {
    setApplyToOthers(true);
  };

  return (
    <Grid xs={12}>
      <Grid item container spacing={1} alignItems="flex-end">
        <Grid item xs={12} mb={3}>
          <Grid container spacing={1}>
            <Grid container xs={6}>
              <Grid item>
                <L2Selector onSelectHandler={setSubnetwork} />
              </Grid>
              <Grid item>
                <L3Selector
                  l2Selection={subnetwork}
                  onSelectHandler={setUnitType}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} xs={6} alignItems="center" justifyContent="flex-end">
              <Grid item>
                <Button
                  appearance="subtle"
                  onClick={handlePreviewQuestions}
                  iconBefore={<WatchIcon label="eye" />}
                  isDisabled={!networkData.l1Id || !networkData.l2Id || !networkData.l3Id}
                >
                  Preview Questions
                </Button>
              </Grid>
              <Grid item>
                <ReadOnlyWrapper>
                  <Button
                    onClick={handleApplyToOther}
                    appearance={applyToOtherUnitAppearance}
                    iconBefore={<CheckIcon label="check" />}
                    isDisabled={!networkData.l1Id || !networkData.l2Id || !networkData.l3Id || isSuperAdminRead() || editDisabled}
                  >
                    Apply to Other Unit Types
                  </Button>
                </ReadOnlyWrapper>
              </Grid>
              {isSuperAdmin() && (
                <Grid item>
                  <ModalProvider>
                    <CopyQuestionsToOtherNetworks {...networkData} editDisabled={editDisabled} />
                  </ModalProvider>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {
          lifestyleTemplateSelection
            ? (
              <Grid item xs={12} mb={3}>
                <Typography>
                  This unit type is using the <b>{lifestyleTemplateSelection.name}</b> version of Lifestyle Questions for residents. These questions cannot be modified. Please contact <Link href={`mailto:${CLIENTS_EMAIL_ADDRESS}`}><b>{CLIENTS_EMAIL_ADDRESS}</b></Link> for assistance with modifying these questions.
                </Typography >
              </Grid>
            ) : null
        }
        {subnetwork && unitType && (
          <Grid item container spacing={5}>
            <Grid item xs={12}>
              <SliderQuestionsTable
                onSave={onSave}
                onReorder={onReorder}
                onRemove={setRemoveInfo}
                questions={questions.SLIDER || []}
                showTagColumn={showTagColumn}
                editDisabled={editDisabled || isReadOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <SelectQuestionsTable
                onSave={onSave}
                onReorder={onReorder}
                onRemove={setRemoveInfo}
                questions={questions.SELECT || []}
                showTagColumn={showTagColumn}
                editDisabled={editDisabled || isReadOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <FreeTextQuestionsTable
                questions={questions.FREE_TEXT || []}
                onSave={onSave}
                onRemove={setRemoveInfo}
                onReorder={onReorder}
                showTagColumn={showTagColumn}
                editDisabled={editDisabled || isReadOnly}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
      <QuestionRemovalModal data={removeInfo} onRemove={onRemove} onHide={setRemoveInfo} />
      <PreviewQuestionsModal questions={questions} isOpen={previewQuestions} onHide={setPreviewQuestions} />
      <ApplyToOthersModal {...networkData} visible={applyToOthers} onHide={setApplyToOthers} />
    </Grid>
  );
};
